import CirculationCompGrid from 'components/Incident/Circulation/CirculationCompGrid'
import { useUser } from 'contexts/UserContext'
import useFetch from 'hooks/useFetch'
import { useToast } from 'hooks/useToast'
import { useEffect } from 'react'
import * as XLSX from 'xlsx'

export default function CirculationViewGrid() {
  const { showToast } = useToast()
  const { user } = useUser()

  // useFetch(): hook personalizado para realizar solicitudes GET, POST, PUT, DELETE
  const [fetchedIncidents, fetchIncidents] = useFetch()
  const [deletedIncident, deleteIncident] = useFetch()

  // CRUD: GET
  const handleRefresh = async () => {
    const apiCallGet = {
      url: 'incidents/circulation',
      method: 'GET',
      messageKo: '¡Error cargando siniestros!'
    }
    await fetchIncidents(apiCallGet)
  }

  // Ejecutar GET
  useEffect(() => {
    handleRefresh()
  }, [])

  // Analizar archivo Excel
  const handleAnalyzeExcel = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (event) => {
        try {
          const arrayBuffer = event.target.result
          const workbook = XLSX.read(arrayBuffer, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]

          // Convertir hoja a JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            raw: false,
            cellDates: true
          })

          // Validar nombre de la hoja
          if (sheetName !== 'SINIESTROS DE CIRCULACIÓN') {
            throw new Error(
              "La hoja que usted está intentando importar no tiene el nombre 'SINIESTROS DE CIRCULACIÓN'. Por lo tanto, la importación no es posible. Por favor, revise si está importando el archivo en el lugar correcto. Si el problema persiste, contacte con soporte en 'suport@gestinet.com'."
            )
          }

          // Mostrar filas Excel sin formatear
          // console.log('jsondata: ', jsonData)

          const logs = []
          const seenIncidentNumbers = new Set() // Para detectar duplicados

          // Formatear datos
          const formattedItems = jsonData
            .map((item) => {
              const rowNumber = item.__rowNum__ + 1
              const row = JSON.stringify(item)

              // Omitir filas con 'Total pagado' o 'Total pagos'
              if (row.includes('Total pagado') || row.includes('Total pagos')) {
                logs.push({
                  type: 'warning',
                  message: `La fila ${rowNumber} no se importará, ya que solo contiene información del pago total.`
                })
                return null
              }

              // Validar 'Nº de Siniestro': Si la fila no tiene número de siniestro, se omite la importación
              if (!item['Nº de Siniestro']) {
                logs.push({
                  type: 'warning',
                  message: `La fila ${rowNumber} no tiene un número de siniestro, por lo que será omitida y no se importará. Si desea incluir esta fila, por favor verifique el archivo y agregue el número de siniestro.`
                })
                return null
              }

              // Detectar duplicados
              if (seenIncidentNumbers.has(item['Nº de Siniestro'])) {
                logs.push({
                  type: 'error',
                  message: `El siniestro '${item['Nº de Siniestro']}' en la fila ${rowNumber} ya existe. Por favor, revise los datos duplicados antes de importar.`
                })
                return null
              }
              seenIncidentNumbers.add(item['Nº de Siniestro'])

              // Validar fecha 'F. Ocurrencia'
              if (item['F. Ocurrencia']) {
                const slashCount = (item['F. Ocurrencia'].match(/\//g) || [])
                  .length
                const parsedDate = new Date(item['F. Ocurrencia'])

                if (slashCount > 2 || isNaN(parsedDate.getTime())) {
                  logs.push({
                    type: 'error',
                    message: `El campo 'F. Ocurrencia' en la fila ${rowNumber} tiene un formato incorrecto. Asegúrese de que esté en el formato 'día/mes/año', sin espacios adicionales ni caracteres especiales, o bien, puede dejar el campo vacío.`
                  })
                  return null
                }

                const [month, day, year] = item['F. Ocurrencia']?.split('/')
                item['F. Ocurrencia'] = `${day.padStart(
                  2,
                  '0'
                )}/${month.padStart(2, '0')}/20${year}`
              }

              // Validar 'Hora Ocurrencia'
              if (
                item['Hora Ocurrencia'] !== undefined &&
                item['Hora Ocurrencia'] !== null
              ) {
                const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5]?[0-9])$/

                if (!timeRegex.test(item['Hora Ocurrencia'].trim())) {
                  logs.push({
                    type: 'error',
                    message: `El campo 'Hora Ocurrencia' en la fila ${rowNumber} tiene un formato incorrecto. Asegúrese de que esté en el formato 'HH:MM' (por ejemplo, '14:30'), sin espacios adicionales al principio, al final ni entre los números. También puede dejar el campo vacío si no dispone de la hora exacta.`
                  })
                  return null
                }
              }

              // Validar 'Coordenadas': Debe ser null o seguir el formato '41.12345, 41.123456789' o '41.123456789,41.12345', para evitar errores al mostrar en el mapa
              if (item.Coordenadas === '') {
                item.Coordenadas = null
              }

              if (item.Coordenadas) {
                const coordinatesRegex = /^[0-9]*\.[0-9]+, ?[0-9]*\.[0-9]+$/

                if (!coordinatesRegex.test(item.Coordenadas)) {
                  logs.push({
                    type: 'error',
                    message: `El campo 'Coordenadas' en la fila ${rowNumber} tiene un formato incorrecto. Por favor, utilice el formato 'número.número,número.número' o 'número.número, número.número' (ejemplo: '41.40338,2.17403' o '41.40338, 2.17403') y asegúrese de que no haya espacios adicionales al principio, al final ni entre los números, ni caracteres especiales. También puede dejar el campo vacío si no dispone de las coordenadas.`
                  })
                  return null
                }
              }

              // Validar 'Pagos': Debe ser null o un float, eliminando el símbolo de euro y ajustando puntos y comas para la base de datos
              if (item.Pagos === '') {
                item.Pagos = null
              }

              if (item.Pagos) {
                item.Pagos = item.Pagos.replace('€', '').replace(',', '')
                item.Pagos = parseFloat(item.Pagos)

                if (isNaN(item.Pagos)) {
                  logs.push({
                    type: 'error',
                    message: `El campo 'Pagos' en la fila ${rowNumber} no es un valor numérico válido.`
                  })
                  return null
                }
              }

              // Si la fila pasa todas las validaciones sin errores, se confirma su importación
              logs.push({
                type: 'import',
                message: `El siniestro '${item['Nº de Siniestro']}' en la fila ${rowNumber} está listo para importarse correctamente.`
              })

              // Mostrar cada fila del Excel por separado
              // console.log('\n\n\n🚀 ~ .map ~ item:', item)

              return Object.values({
                status: safeTrim(item.Estatus),
                incident_number: safeTrim(item['Nº de Siniestro']),
                agency_reference: safeTrim(item['Referencia agencia']),
                tus_reference: safeTrim(item['Referencia TUS']),
                incident_risk: safeTrim(item['Riesgo siniestro']),
                occurrence_date: item['F. Ocurrencia'],
                occurrence_time: item['Hora Ocurrencia'],
                coordinates: item.Coordenadas,
                driver: safeTrim(item.Conductor),
                position: safeTrim(item['Posición']),
                payments: item.Pagos,
                description: safeTrim(item['Descripción']),
                observations: safeTrim(item.Observaciones),
                type: 'circulation'
              })
            })
            .filter(Boolean) // Eliminar valores nulos

          resolve({
            logs,
            data: formattedItems
          })
        } catch (error) {
          console.error('Circulation Incidents Grid View - Error:', error)
          reject(error) // Rechazar si hay error
        }
      }

      reader.onerror = (error) => {
        reject(error) // Rechazar si falla la lectura del archivo
      }

      reader.readAsArrayBuffer(file)
    })
  }

  // Recorta espacios solo si el valor es una cadena
  const safeTrim = (value) => (typeof value === 'string' ? value.trim() : value)

  // CRUD: DELETE
  const handleDelete = async (id) => {
    const apiCallDelete = {
      url: `incidents/circulation/${user.id}/${id}`,
      method: 'DELETE',
      messageKo: '¡Error eliminando siniestro!'
    }
    await deleteIncident(apiCallDelete)
  }

  // Mostrar mensaje API: DELETE (notificación de éxito o error)
  useEffect(() => {
    if (deletedIncident.isCalled) {
      if (deletedIncident.message) {
        showToast(deletedIncident.message, 'success')
        handleRefresh()
      }

      if (deletedIncident.error) {
        showToast(deletedIncident.error, 'error')
      }
    }
  }, [deletedIncident.loading])

  return (
    <CirculationCompGrid
      user={user}
      data={fetchedIncidents?.data}
      handleRefresh={handleRefresh}
      handleAnalyzeExcel={handleAnalyzeExcel}
      handleDelete={handleDelete}
    />
  )
}
